/**
 * Servicio para operaciones CRUD de productos vía API (MongoDB)
 */
import ApiService from './ApiService';

/**
 * Obtiene productos (opcionalmente filtrados)
 * @param {{ categoria?: string, destacado?: boolean|string }} [filtros]
 * @returns {Promise<Array>} Lista de productos
 */
export const obtenerProductos = async (filtros = {}, { withAuth = false } = {}) => {
  try {
    const params = new URLSearchParams();
    if (filtros.categoria) {
      params.set('categoria', filtros.categoria);
    }
    if (filtros.destacado !== undefined && filtros.destacado !== null && filtros.destacado !== '') {
      params.set('destacado', String(filtros.destacado));
    }
    const query = params.toString();
    const path = query ? `/productos?${query}` : '/productos';
    const respuesta = await ApiService.get(path, { withAuth });
    return Array.isArray(respuesta?.data) ? respuesta.data : [];
  } catch (error) {
    console.error('Error al obtener productos:', error);
    throw new Error('No se pudieron cargar los productos');
  }
};

/**
 * Obtiene un producto por ID
 * @param {string} id - ID del producto
 * @returns {Promise<Object|null>} Producto o null si no existe
 */
export const obtenerProductoPorId = async (id, { withAuth = false } = {}) => {
  if (!id) {
    throw new Error('ID de producto no válido');
  }

  try {
    const respuesta = await ApiService.get(`/productos/${id}`, { withAuth });
    return respuesta?.data || null;
  } catch (error) {
    if (error.status === 404) {
      return null;
    }
    console.error('Error al obtener producto:', error);
    throw new Error('Error al cargar el producto');
  }
};

/**
 * Crea un nuevo producto
 * @param {Object} datos - Datos del producto
 * @returns {Promise<string>} ID del producto creado
 */
export const crearProducto = async (datos) => {
  try {
    const datosLimpios = {};
    Object.keys(datos).forEach((key) => {
      const valor = datos[key];
      if (valor !== null && valor !== undefined && valor !== '') {
        datosLimpios[key] = valor;
      } else if (key === 'destacado' && typeof valor === 'boolean') {
        datosLimpios[key] = valor;
      }
    });

    const respuesta = await ApiService.post('/productos', datosLimpios, { withAuth: true });
    const creado = respuesta?.data;
    if (!creado?.id) {
      throw new Error('El servicio no devolvió el ID del producto');
    }
    return creado.id;
  } catch (error) {
    console.error('Error completo al crear producto:', error);
    throw new Error(error.message || 'Error al crear el producto');
  }
};

/**
 * Actualiza un producto
 * @param {string} id - ID del producto
 * @param {Object} datos - Datos a actualizar
 * @returns {Promise<void>}
 */
export const actualizarProducto = async (id, datos) => {
  if (!id) {
    throw new Error('ID de producto no válido');
  }

  try {
    const datosLimpios = {};
    Object.keys(datos).forEach((key) => {
      const valor = datos[key];
      if (valor !== null && valor !== undefined && valor !== '') {
        datosLimpios[key] = valor;
      } else if (key === 'destacado' && typeof valor === 'boolean') {
        datosLimpios[key] = valor;
      } else if (key === 'costo' && valor === null) {
        datosLimpios[key] = null;
      }
    });

    await ApiService.put(`/productos/${id}`, datosLimpios, { withAuth: true });
  } catch (error) {
    console.error('Error al actualizar producto:', error);
    throw new Error(error.message || 'Error al actualizar el producto');
  }
};

/**
 * Elimina un producto
 * @param {string} id - ID del producto
 * @returns {Promise<void>}
 */
export const eliminarProducto = async (id) => {
  if (!id) {
    throw new Error('ID de producto no válido');
  }

  try {
    await ApiService.del(`/productos/${id}`, { withAuth: true });
  } catch (error) {
    console.error('Error al eliminar producto:', error);
    throw new Error(error.message || 'Error al eliminar el producto');
  }
};

/**
 * Actualiza precios de múltiples productos
 * @param {Array<string>} ids - IDs de los productos
 * @param {Object} precios - Objeto con precio y/o preciomenor
 * @returns {Promise<void>}
 */
export const actualizarPreciosMasivo = async (ids, precios) => {
  if (!ids || ids.length === 0) {
    throw new Error('No hay productos para actualizar');
  }

  try {
    const body = { ids };
    if (precios.precio !== null && precios.precio !== undefined && precios.precio !== '') {
      body.precio = Number(precios.precio);
    }
    if (precios.preciomenor !== null && precios.preciomenor !== undefined && precios.preciomenor !== '') {
      body.preciomenor = Number(precios.preciomenor);
    }

    if (body.precio === undefined && body.preciomenor === undefined) {
      throw new Error('No hay precios para actualizar');
    }

    await ApiService.patch('/productos/precios-masivo', body, { withAuth: true });
  } catch (error) {
    console.error('Error al actualizar precios:', error);
    throw new Error(error.message || 'Error al actualizar los precios');
  }
};

/**
 * Actualiza el costo de múltiples productos
 * @param {Array<string>} ids - IDs de los productos
 * @param {number|string} costo - Costo a aplicar
 * @returns {Promise<void>}
 */
export const actualizarCostosMasivo = async (ids, costo) => {
  if (!ids || ids.length === 0) {
    throw new Error('No hay productos para actualizar');
  }

  if (costo === null || costo === undefined || costo === '') {
    throw new Error('No hay costo para actualizar');
  }

  try {
    await ApiService.patch(
      '/productos/precios-masivo',
      { ids, costo: Number(costo) },
      { withAuth: true }
    );
  } catch (error) {
    console.error('Error al actualizar costos:', error);
    throw new Error(error.message || 'Error al actualizar los costos');
  }
};

/**
 * Obtiene el historial de movimientos de stock de un producto
 * @param {string} id - ID del producto
 * @param {{ limit?: number }} [opciones]
 * @returns {Promise<Array>}
 */
export const obtenerMovimientosStock = async (id, opciones = {}) => {
  if (!id) {
    throw new Error('ID de producto no válido');
  }

  try {
    const params = new URLSearchParams();
    if (opciones.limit != null) {
      params.set('limit', String(opciones.limit));
    }
    const query = params.toString();
    const path = query
      ? `/productos/${id}/movimientos-stock?${query}`
      : `/productos/${id}/movimientos-stock`;
    const respuesta = await ApiService.get(path, { withAuth: true });
    return Array.isArray(respuesta?.data) ? respuesta.data : [];
  } catch (error) {
    console.error('Error al obtener movimientos de stock:', error);
    throw new Error(error.message || 'No se pudo cargar el historial de stock');
  }
};
